<template>
  <div id="map" class="map">
    <v-progress-linear indeterminate v-if="dataLoading" height="4"
      >Collecting Data</v-progress-linear
    >
    <v-slide-x-reverse-transition>
      <v-alert
        v-show="!dataLoaded && !dataLoading"
        :value="true"
        type="info"
        class="steps"
      >
        <div class="text-lg-center">{{ instructionText }}</div>

        <div>
          <v-btn
            small
            flat
            @click="drawMode = !drawMode"
            color="white"
            outline
            >{{
              drawMode ? "change to selection mode" : "change to draw mode"
            }}</v-btn
          >
        </div>
      </v-alert>
    </v-slide-x-reverse-transition>

    <div v-if="tour" class="tour">
      <spatial-stats-tour @stop-tour="stopTour()"></spatial-stats-tour>
    </div>

    <div v-if="dataLoaded" class="report-card-container">
      <div class="report-card">
        <report-card
          :data="{ geo: this.nest[this.nest.length - 1], series: this.series }"
          :inset-geo="this.nest[this.nest.length - 2]"
          :base-map="baseMap"
          @close="closeStatViewer"
        ></report-card>
        <div class="mt-2">
          <v-card>
            <v-card-text>
              <h4>Change the basemap</h4>
              <div class="d-flex">
                <div
                  v-for="(item, i) in baseMaps"
                  :key="i"
                  @click="baseMap = item"
                  style="cursor: pointer;"
                >
                  <div>{{ item.name }}</div>
                  <v-icon>{{
                    item === baseMap
                      ? "radio_button_checked"
                      : "radio_button_unchecked"
                  }}</v-icon>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>

    <v-card raised class="control-card" :width="dataLoaded ? 400 : 300" dark>
      <div class="action-buttons">
        <v-btn small color="warning" outline @click="exit" flat width="200"
          ><v-icon>close</v-icon>&nbsp;exit tool</v-btn
        >
      </div>
      <v-divider></v-divider>
      <v-card-text>
        <h3>Selection Layer</h3>
        <v-list>
          <v-list-tile
            v-for="(item, i) in selectionLayers"
            :key="i"
            @click="changeSelectionLayer(item)"
          >
            <v-list-tile-title v-text="item.name"></v-list-tile-title>
            <v-list-tile-action>
              <v-icon>{{
                item.name === selectionLayer
                  ? "radio_button_checked"
                  : "radio_button_unchecked"
              }}</v-icon>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-text v-if="currentStep > -1">
        <h3 style="padding-bottom: 10px;">Watershed/Waterbody</h3>
        <div v-for="(n, index) in nest" :key="index">
          {{
            `${index + 1}. ${
              n.properties.NAME ? n.properties.NAME : "Unknown Name"
            }`
          }}
        </div>
      </v-card-text>
      <v-card-actions class="text-lg-center" v-show="currentStep > -1">
        <div class="action-buttons">
          <v-fade-transition>
            <v-btn small @click="back" flat color="white"
              ><v-icon>keyboard_arrow_left</v-icon>&nbsp;Back</v-btn
            >
          </v-fade-transition>
        </div>
      </v-card-actions>
      <v-card-text v-if="drawMode && drawArea">
        <span>Custom Study Area: </span><span v-html="this.drawArea"></span>
        <v-text-field
          v-model="drawName"
          label="Study Area Name"
          outline
          color="white"
        ></v-text-field>
      </v-card-text>
      <v-divider></v-divider>
      <div class="action-buttons">
        <v-btn
          flat
          outline
          color="#e059a6"
          @click="runStats"
          v-if="!dataLoaded && !dataLoading"
          width="200"
          :disabled="runReportDisabled"
          >create report</v-btn
        >
      </div>
    </v-card>
  </div>
</template>

<script>
import * as mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import * as turf from "@turf/turf";
import performStats from "@/api/spatialStats";
import { union } from "@turf/turf";
import ReportCard from "../components/ReportCard.vue";
import SpatialStatsTour from "@/components/SpatialStatsTour";

export default {
  name: "SpatialStats",

  components: {
    ReportCard,
    SpatialStatsTour,
  },

  data() {
    return {
      tour: false,
      baseMap: null,
      selectionLayers: [
        { name: "Watershed/Waterbody" },
        { name: "Municipal Boundaries" },
      ],
      selectionLayer: "Watershed/Waterbody",
      drawMode: false,
      drawName: null,
      drawArea: null,
      currentStep: -1,
      dataLoaded: false,
      dataLoading: false,
      nest: [],
      series: [],
      mapOptions: {
        container: "map",
        style: "mapbox://styles/mapbox/light-v10",
        center: [-114, 55],
        zoom: 5,
        maxZoom: 12,
      },
      mapHover: null,
      layerMapHover: null,
      lastBounds: null,
    };
  },

  computed: {
    watersheds() {
      return this.$store.state.map.spatialStatsWatersheds;
    },
    instructionText() {
      if (this.drawMode) return "Use the buttons to the right or ";
      else if (this.currentStep === -1) return "Click on a watershed or ";
      else if (this.currentStep === this.watersheds.length - 1)
        return "Run report or ";
      else return "Click on another region, run report or ";
    },
    baseLayers() {
      return this.$store.state.map.baseLayers;
    },
    baseMaps() {
      return this.$store.state.map.baseMaps;
    },
    watershedMode() {
      return this.selectionLayer === "Watershed/Waterbody";
    },
    runReportDisabled() {
      if (this.drawMode) {
        return !this.drawArea || !this.drawName;
      } else if (this.watershedMode) {
        return this.currentStep === -1;
      } else {
        return this.nest.length === 0;
      }
    },
  },

  watch: {
    drawMode(newVal) {
      this.nest = [];
      this.currentStep = -1;

      if (newVal) {
        this.map.setStyle("mapbox://styles/mapbox/satellite-streets-v11");
      } else {
        this.draw.changeMode("simple_select");
        this.draw.deleteAll();
        this.map.setStyle("mapbox://styles/mapbox/light-v10");
      }
    },
    drawName(newVal) {
      this.nest[0].properties.NAME = newVal;
    },
  },

  methods: {
    exit() {
      this.$router.push({ name: "Riparian" });
    },
    back() {
      this.deselectBasin();
    },
    stopTour() {
      localStorage["riparianSpatialStatsTour"] = true;
      this.tour = false;
    },

    toggleLayer(layer) {
      if (layer) {
        // Turn off watersheds
        this.watersheds.forEach((ws) => {
          this.map.setLayoutProperty(ws.mapID, "visibility", "none");
          this.map.setLayoutProperty(
            `${ws.mapID}-boundary`,
            "visibility",
            "none"
          );
          this.map.setLayoutProperty(
            `${ws.mapID}-labels`,
            "visibility",
            "none"
          );
        });

        // Turn on layer
        this.map.setLayoutProperty(layer.name, "visibility", "visible");
        this.map.setLayoutProperty(
          `${layer.name}-labels`,
          "visibility",
          "visible"
        );
        this.map.setLayoutProperty(
          `${layer.name}-boundary`,
          "visibility",
          "visible"
        );
      } else {
        // Turn off layer
        this.map.setLayoutProperty(this.selectionLayer, "visibility", "none");
        this.map.setLayoutProperty(
          `${this.selectionLayer}-labels`,
          "visibility",
          "none"
        );
        this.map.setLayoutProperty(
          `${layer.name}-boundary`,
          "visibility",
          "none"
        );

        // Turn on watersheds
        this.changeBasinVisibility(-1, 0);
      }
    },

    changeSelectionLayer(layer) {
      this.nest = [];
      this.currentStep = -1;
      if (layer.name === "Watershed/Waterbody") {
        this.toggleLayer();
        // Needs to be last
        this.selectionLayer = layer.name;
      } else {
        this.selectionLayer = layer.name;
        this.toggleLayer(layer);
      }
    },

    addLayers() {
      let firstSymbolId;
      let layers = this.map.getStyle().layers;
      for (let i = 0; i < layers.length; i++) {
        if (layers[i].type === "symbol") {
          firstSymbolId = layers[i].id;
          break;
        }
      }

      // Add a hillshade
      this.map.addSource("dem", {
        type: "raster-dem",
        url: "mapbox://mapbox.terrain-rgb",
      });
      this.map.addLayer(
        {
          id: "dem-hillshade",
          source: "dem",
          type: "hillshade",
          maxzoom: 13,
        },
        firstSymbolId
      );

      if (this.drawMode) {
        if (!this.draw) {
          this.draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: {
              polygon: true,
              trash: true,
            },
            defaultMode: "simple_select",
          });
          this.map.addControl(this.draw);

          this.map.on("draw.create", this.updateDrawPolys);
          this.map.on("draw.delete", this.updateDrawPolys);
          this.map.on("draw.update", this.updateDrawPolys);
        }

        this.draw.changeMode("draw_polygon");

        return;
      }

      // Watersheds
      let self = this;
      this.watersheds.forEach((ws, wsIndex) => {
        this.map.addSource(ws.mapID, {
          type: "vector",
          tiles: [ws.url],
        });
        ws.paint["fill-opacity"] = [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          0.4,
          0.2,
        ];
        ws.paint["fill-color"] = [
          "match",
          ["get", "hasdata"],
          0,
          "#000",
          1,
          "#1c77ed",
          "#000",
        ];
        this.map.addLayer(
          {
            id: ws.mapID,
            type: "fill",
            source: ws.mapID,
            "source-layer": ws.layer,
            paint: ws.paint,
            layout: {
              visibility: wsIndex === 0 ? "visible" : "none",
            },
          },
          firstSymbolId
        );
        this.map.addLayer(
          {
            id: `${ws.mapID}-boundary`,
            type: "line",
            source: ws.mapID,
            "source-layer": ws.layer,
            paint: {
              "line-color": "black",
              "line-width": 2,
            },
            layout: {
              visibility: wsIndex === 0 ? "visible" : "none",
            },
          },
          firstSymbolId
        );
        this.map.addLayer({
          id: `${ws.mapID}-labels`,
          type: "symbol",
          source: ws.mapID,
          "source-layer": ws.layer,
          layout: {
            "text-field": ["get", ws.nameField],
            visibility: wsIndex === 0 ? "visible" : "none",
          },
          paint: {
            "text-color": "#fff",
            "text-halo-color": "black",
            "text-halo-width": 1,
            "text-opacity": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              1,
              0,
            ],
          },
        });

        // Watershed Events
        this.map.on("mousemove", ws.mapID, function(e) {
          if (e.features.length > 0 && e.features[0].properties.hasdata) {
            self.map.getCanvas().style.cursor = "pointer";
            if (self.mapHover !== null) {
              if (!this.dataLoaded) {
                self.map.setFeatureState(
                  {
                    source: ws.mapID,
                    sourceLayer: ws.layer,
                    id: self.mapHover,
                  },
                  { hover: false }
                );
              }
            }
            self.mapHover = e.features[0].id;
            self.map.setFeatureState(
              {
                source: ws.mapID,
                sourceLayer: ws.layer,
                id: self.mapHover,
              },
              { hover: true }
            );
          }
        });
        this.map.on("mouseleave", ws.mapID, function() {
          self.map.getCanvas().style.cursor = "";
          if (!this.dataLoaded) {
            self.map.setFeatureState(
              {
                source: ws.mapID,
                sourceLayer: ws.layer,
                id: self.mapHover,
              },
              { hover: false }
            );
          }
          self.mapHover = null;
        });
        this.map.on("click", ws.mapID, this.selectBasin);
      });

      // Municipalities
      const municipalities = this.baseLayers.find(
        (layer) => layer.name === "Municipal Boundaries"
      );

      this.map.addSource(municipalities.name, {
        type: "vector",
        tiles: [municipalities.url],
      });

      this.map.addLayer(
        {
          id: municipalities.name,
          type: "fill",
          source: municipalities.name,
          "source-layer": municipalities.sourceLayer,
          layout: {
            visibility: municipalities.toggled ? "visible" : "none",
          },
          paint: {
            "fill-outline-color": "rgba(0, 0, 0, 0.3)",
            "fill-color": "#819a99",
            "fill-opacity": [
              "case",
              ["boolean", ["feature-state", "hover"], false],
              0.4,
              0,
            ],
          },
        },
        firstSymbolId
      );

      this.map.addLayer(
        {
          id: `${municipalities.name}-boundary`,
          type: municipalities.type,
          source: municipalities.name,
          "source-layer": municipalities.sourceLayer,
          layout: {
            visibility: municipalities.toggled ? "visible" : "none",
          },
          paint: municipalities.paint,
        },
        firstSymbolId
      );

      this.map.addLayer({
        id: `${municipalities.name}-labels`,
        type: "symbol",
        source: municipalities.name,
        "source-layer": municipalities.sourceLayer,
        layout: {
          "text-field": ["get", municipalities.labelField],
          visibility: municipalities.toggled ? "visible" : "none",
          "text-padding": 50,
          ...(municipalities.labelLayout ? municipalities.labelLayout : {}),
        },
        paint: {
          "text-halo-color": "#fff",
          "text-halo-width": 1,
          ...(municipalities.labelPaint ? municipalities.labelPaint : {}),
        },
      });

      this.map.on("mousemove", municipalities.name, (e) => {
        if (e.features.length > 0) {
          this.map.getCanvas().style.cursor = "pointer";
          if (this.layerMapHover !== null) {
            this.map.setFeatureState(
              {
                source: municipalities.name,
                sourceLayer: municipalities.sourceLayer,
                id: this.layerMapHover,
              },
              { hover: false }
            );
          }
          this.layerMapHover = e.features[0].id;
          this.map.setFeatureState(
            {
              source: municipalities.name,
              sourceLayer: municipalities.sourceLayer,
              id: this.layerMapHover,
            },
            { hover: true }
          );
        }
      });
      this.map.on("mouseleave", municipalities.name, () => {
        this.map.getCanvas().style.cursor = "";
        this.map.setFeatureState(
          {
            source: municipalities.name,
            sourceLayer: municipalities.sourceLayer,
            id: this.layerMapHover,
          },
          { hover: false }
        );
        this.layerMapHover = null;
      });
      this.map.on("click", municipalities.name, this.selectLayer);
    },

    initMap() {
      mapboxgl.accessToken = this.$store.state.map.mapbox_gl_token;
      this.map = new mapboxgl.Map(this.mapOptions);

      const self = this;
      this.map.on("load", () => {
        this.addLayers();

        this.map.on("style.load", function() {
          self.addLayers();
        });
      });
    },

    updateDrawPolys() {
      if (!this.drawMode) {
        this.$swal.fire({
          title: "You're not in Draw Mode",
          icon: "error",
          timer: 1300,
          showConfirmButton: false,
        });

        setTimeout(() => {
          this.draw.changeMode("simple_select");
          this.draw.deleteAll();
        }, 800);

        return;
      }

      const data = this.draw.getAll();

      if (data.features.length > 0) {
        const area = turf.area(data);

        if (area < 1000) {
          const roundedArea = (Math.round(area * 10) / 10).toString();
          this.drawArea = `${roundedArea.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ","
          )} m<sup>2</sup>`;
        } else {
          const roundedArea = (
            Math.round((area / 1000000) * 100) / 100
          ).toString();
          this.drawArea = `${roundedArea.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ","
          )} km<sup>2</sup>`;
        }

        // Add all features into a multipolygon
        const feature = { ...data.features[0] };
        feature.geometry = { type: "MultiPolygon", coordinates: [] };

        data.features.forEach((ft) => {
          feature.geometry.coordinates.push(ft.geometry.coordinates);
        });

        if (this.drawName) feature.properties.NAME = this.drawName;

        this.nest = [feature];
      } else {
        this.drawArea = null;
        this.drawName = null;
      }
    },

    filterByHUC(wsID) {
      this.map.setFilter(wsID, null);
      this.map.setFilter(`${wsID}-boundary`, null);
      this.map.setFilter(`${wsID}-labels`, null);

      if (this.nest.length === 0) return;

      const props = this.nest[this.nest.length - 1].properties;

      const hucLevel = props.HUC_6 ? "HUC_6" : "HUC_2";

      const hucID = props.HUC_6
        ? props.HUC_6
        : props.HUC_2
        ? props.HUC_2
        : null;

      if (hucID) {
        this.map.setFilter(wsID, [
          "match",
          ["get", hucLevel],
          [hucID],
          true,
          false,
        ]);
        this.map.setFilter(`${wsID}-boundary`, [
          "match",
          ["get", hucLevel],
          [hucID],
          true,
          false,
        ]);
        this.map.setFilter(`${wsID}-labels`, [
          "match",
          ["get", hucLevel],
          [hucID],
          true,
          false,
        ]);
      }
    },

    changeBasinVisibility(oldVal, newVal) {
      const previousWS = this.watersheds[oldVal];
      const nextWS = this.watersheds[newVal];

      if (previousWS) {
        this.map.setLayoutProperty(previousWS.mapID, "visibility", "none");
        this.map.setLayoutProperty(
          `${previousWS.mapID}-boundary`,
          "visibility",
          "none"
        );
        this.map.setLayoutProperty(
          `${previousWS.mapID}-labels`,
          "visibility",
          "none"
        );
      }
      if (nextWS) {
        this.map.setLayoutProperty(nextWS.mapID, "visibility", "visible");
        this.map.setLayoutProperty(
          `${nextWS.mapID}-boundary`,
          "visibility",
          "visible"
        );
        this.map.setLayoutProperty(
          `${nextWS.mapID}-labels`,
          "visibility",
          "visible"
        );

        // Filter by HUC ID
        this.filterByHUC(nextWS.mapID);
      }
    },
    fitMapBounds(coords) {
      let coordinates = [];
      const nextCoords = (coords) => {
        if (coords.length > 2) {
          coords.forEach((coord) => nextCoords(coord));
        } else {
          coordinates = [coords, ...coordinates];
        }
      };
      coords.forEach((coord) => nextCoords(coord));

      let bounds = coordinates.reduce(function(bounds, coord) {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));

      this.map.fitBounds(bounds, {
        padding: 30,
      });
    },
    mergeGeos(e) {
      // Merge all features with the selected ID
      const featureID = e.features[0].id;
      const featureName = e.features[0].layer.id;
      const featureLayer = e.features[0].layer["source-layer"];

      const allFeatures = this.map.querySourceFeatures(featureName, {
        sourceLayer: featureLayer,
        filter: ["==", ["id"], featureID],
      });

      const mergedGeos = allFeatures.reduce(union);

      mergedGeos.properties = e.features[0].properties;
      mergedGeos.layer = e.features[0].layer;
      mergedGeos.id = e.features[0].id;

      return mergedGeos;
    },
    selectLayer(e) {
      const mergedGeos = this.mergeGeos(e);

      this.nest = [mergedGeos];
      this.fitMapBounds(mergedGeos.geometry.coordinates);
    },
    selectBasin(e) {
      if (
        !this.dataLoaded &&
        !this.dataLoading &&
        e.features[0].properties.hasdata === 1 &&
        e.features[0].properties.NAME !==
          (this.nest[this.currentStep]
            ? this.nest[this.currentStep].properties.NAME
            : null)
      ) {
        const mergedGeos = this.mergeGeos(e);

        if (this.currentStep === this.watersheds.length - 1) {
          this.nest = this.nest.slice(0, this.nest.length - 1);
        } else {
          this.currentStep++;
        }

        this.nest.push(mergedGeos);
        this.mapHover = null;

        // Fly to the selected basin and load the nested basins
        this.fitMapBounds(mergedGeos.geometry.coordinates);
        if (this.currentStep + 1 < this.watersheds.length)
          this.changeBasinVisibility(this.currentStep, this.currentStep + 1);
      }
    },
    deselectBasin() {
      this.mapHover = null;
      this.currentStep--;
      if (this.currentStep < 0) {
        this.map.flyTo({
          center: [-114, 55],
          zoom: 5,
        });
      } else {
        this.fitMapBounds(this.nest[this.currentStep].geometry.coordinates);
      }

      this.nest = this.nest.slice(0, this.nest.length - 1);
      this.changeBasinVisibility(this.currentStep + 2, this.currentStep + 1);
    },
    closeStatViewer() {
      this.dataLoaded = false;
      this.dataLoading = false;
      if (!this.drawMode && this.watershedMode) {
        this.map.setFeatureState(
          {
            source: this.nest[this.nest.length - 1].layer.id,
            sourceLayer: this.nest[this.nest.length - 1].layer["source-layer"],
            id: this.nest[this.nest.length - 1].id,
          },
          { hover: false }
        );
        if (this.currentStep + 1 < this.watersheds.length)
          this.changeBasinVisibility(this.currentStep, this.currentStep + 1);
      }
    },
    runStats() {
      this.dataLoading = true;

      if (!this.drawMode && this.watershedMode) {
        try {
          if (this.currentStep + 1 < this.watersheds.length)
            this.changeBasinVisibility(this.currentStep + 1, this.currentStep);
          this.map.setFeatureState(
            {
              source: this.nest[this.nest.length - 1].layer.id,
              sourceLayer: this.nest[this.nest.length - 1].layer[
                "source-layer"
              ],
              id: this.nest[this.nest.length - 1].id,
            },
            { hover: true }
          );
        } catch (err) {
          this.dataLoading = false;

          this.$swal.fire({
            icon: "error",
            text: "There was an error",
          });

          throw new Error(err);
        }
      }

      performStats(this.nest[this.nest.length - 1].geometry)
        .then((response) => {
          this.series = response.map((e) => {
            return Math.round(parseFloat(e) * 100) / 100;
          });

          if (
            this.series.reduce((i, prv) => {
              return i + prv;
            }) === 0
          ) {
            this.$swal.fire({
              icon: "warning",
              text: "No Assessed Area found",
            });
          } else this.dataLoaded = true;
        })
        .catch((err) => {
          this.$swal.fire({
            icon: "error",
            text: err,
          });
          this.closeStatViewer();
        })
        .finally(() => (this.dataLoading = false));
    },
  },

  beforeMount() {
    this.tour = !localStorage["riparianSpatialStatsTour"];
    this.baseMap = this.baseMaps[0];
  },

  mounted() {
    this.initMap();
    setTimeout(() => {
      this.lastBounds = this.map.getBounds();
    }, 1000);
  },

  beforeRouteLeave(to, from, next) {
    this.$swal
      .fire({
        icon: "question",
        title: "Confirm",
        text: "Leave the Summary Tool?",
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      })
      .then((res) => {
        if (res.value) next();
        else next(false);
      });
  },
};
</script>

<style scoped>
.map {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  color: black;
}
.back-icon {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1;
}
.steps {
  position: absolute;
  top: 10px;
  right: 45px;
  z-index: 1;
}
.control-card {
  position: absolute;
  top: 20px;
  left: 20px;
  max-height: 800px;
  overflow-y: auto;
  z-index: 1;
}
.report-card-container {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 4;
}
.report-card {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
}
.tour {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 5;
}
.action-buttons {
  width: 100%;
  text-align: center;
}
#app {
  font-family: Poppins, Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}
</style>
